<template>
  <div class="dashboard-view">
    <!-- Page Header -->
    <div class="mb-6">
      <h1 class="text-2xl sm:text-3xl font-bold text-primary-text dark:text-primary-dark-text mb-2">
        Dashboard
      </h1>
      <p class="text-secondary-text dark:text-secondary-dark-text">
        Welcome back! Here's an overview of your account and activities.
      </p>
    </div>

    <!-- Loading/Error States -->
    <LoadingState 
      :is-loading="loading" 
      type="dashboard" 
      text="Loading dashboard..." 
    />
    
    <div v-if="!loading && error" class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
      <div class="flex items-center">
        <XCircle class="w-5 h-5 text-red-500 mr-2" />
        <span class="text-red-700 dark:text-red-300">{{ error }}</span>
      </div>
      <button @click="fetchDashboardData" class="mt-2 text-sm text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 underline">
        Try again
      </button>
    </div>

    <!-- Content -->
    <div v-if="!loading && !error">
      <!-- Stats Cards (Employee only) -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        <!-- Available Exams Status Card -->
        <div class="card p-6 relative flex flex-col min-h-[140px]">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <ClipboardList class="w-8 h-8 text-blue-500" />
            </div>
            <div class="ml-5 w-0 flex-1">
              <dl>
                <dt class="text-sm font-medium text-secondary-text dark:text-secondary-dark-text truncate">
                  Available Exams
                </dt>
                <dd class="text-lg font-medium text-primary-text dark:text-primary-dark-text">
                  {{ stats.totalExams }}
                </dd>
              </dl>
            </div>
          </div>
          
          <!-- Exam Statuses at bottom left -->
          <div class="pt-4">
            <div class="flex flex-wrap gap-2">
              <div class="flex items-center rounded-md bg-green-50 dark:bg-green-900/20 px-3 py-1.5 border border-green-200 dark:border-green-800">
                <CheckCircle class="w-4 h-4 text-green-600 dark:text-green-400 mr-1.5 flex-shrink-0" />
                <span class="text-xs text-green-700 dark:text-green-300 font-medium mr-1">Passed:</span>
                <span class="text-xs text-primary-text dark:text-primary-dark-text font-semibold">{{ stats.passedExams }}</span>
              </div>
              <div class="flex items-center rounded-md bg-red-50 dark:bg-red-900/20 px-3 py-1.5 border border-red-200 dark:border-red-800">
                <XCircle class="w-4 h-4 text-red-600 dark:text-red-400 mr-1.5 flex-shrink-0" />
                <span class="text-xs text-red-700 dark:text-red-300 font-medium mr-1">Failed:</span>
                <span class="text-xs text-primary-text dark:text-primary-dark-text font-semibold">{{ stats.failedExams }}</span>
              </div>
              <div class="flex items-center rounded-md bg-yellow-50 dark:bg-yellow-900/20 px-3 py-1.5 border border-yellow-200 dark:border-yellow-800">
                <Archive class="w-4 h-4 text-yellow-600 dark:text-yellow-400 mr-1.5 flex-shrink-0" />
                <span class="text-xs text-yellow-700 dark:text-yellow-300 font-medium mr-1">Skipped:</span>
                <span class="text-xs text-primary-text dark:text-primary-dark-text font-semibold">{{ stats.skippedExams }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Current Month Access -->
        <div class="card p-6 relative flex flex-col min-h-[140px]">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <Calendar class="w-8 h-8 text-purple-500" />
            </div>
            <div class="ml-5 w-0 flex-1">
              <dl>
                <dt class="text-sm font-medium text-secondary-text dark:text-secondary-dark-text truncate">
                  {{ currentMonthName }} Access
                </dt>
                <dd class="text-lg font-medium text-primary-text dark:text-primary-dark-text">
                  {{ hasCurrentMonthAccess ? 'Available' : 'No Access' }}
                </dd>
              </dl>
            </div>
          </div>
          
          <!-- Stats positioned at bottom left -->
          <div class="pt-4">
            <div class="flex flex-wrap gap-2">
              <div v-if="hasCurrentMonthAccess" class="flex items-center rounded-md bg-green-50 dark:bg-green-900/20 px-3 py-1.5 border border-green-200 dark:border-green-800">
                <CheckCircle class="w-4 h-4 text-green-600 dark:text-green-400 mr-1.5 flex-shrink-0" />
                <span class="text-xs text-green-700 dark:text-green-300 font-medium">
                  {{ primaryCurrentMonthModule || 'Monthly Module Access' }}
                  <span v-if="currentMonthModuleOverflow > 0" class="ml-2 text-[10px] text-gray-600 dark:text-gray-300">+{{ currentMonthModuleOverflow }}</span>
                </span>
                <template v-if="currentMonthModuleOverflow > 0">
                  <span :title="currentMonthOtherModulesTooltip" class="sr-only">Additional modules</span>
                </template>
              </div>
              <div v-else class="flex items-center rounded-md bg-gray-50 dark:bg-gray-900/20 px-3 py-1.5 border border-gray-200 dark:border-gray-800">
                <XCircle class="w-4 h-4 text-gray-600 dark:text-gray-400 mr-1.5 flex-shrink-0" />
                <span class="text-xs text-gray-700 dark:text-gray-300 font-medium">Not assigned this month</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Module Status Summary -->
        <div class="card p-6 relative flex flex-col min-h-[140px]">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <Users class="w-8 h-8 text-green-500" />
            </div>
            <div class="ml-5 w-0 flex-1">
              <dl>
                <dt class="text-sm font-medium text-secondary-text dark:text-secondary-dark-text truncate">
                  Training Progress
                </dt>
                <dd class="text-lg font-medium text-primary-text dark:text-primary-dark-text">
                  {{ totalAvailableModules }} Total Modules
                </dd>
              </dl>
            </div>
          </div>
          
          <!-- Stats positioned at bottom left -->
          <div class="mt-auto pt-4">
              <div class="flex flex-wrap gap-2">
              <!-- Show month access pills; each pill's tooltip shows module names for that month -->
              <div class="flex flex-wrap items-center gap-2">
                <template v-for="(group, idx) in modulesByMonth" :key="`month-${idx}`">
                  <span :title="group.modules.join(', ')" class="inline-flex items-center px-3 py-1 rounded-md text-xs bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700">
                    {{ group.month }}
                  </span>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Welcome Section with Module Summary -->
      <div class="card p-6 mb-8">
        <div class="flex items-center justify-between mb-6">
          <div>
            <h2 class="text-xl font-semibold text-primary-text dark:text-primary-dark-text mb-2">
              Welcome, {{ userName }}!
            </h2>
            <p class="text-secondary-text dark:text-secondary-dark-text">
              You're logged in as an Employee. Here's your training summary for {{ currentYear }}:
            </p>
          </div>
          <div class="hidden md:block">
            <LayoutDashboard class="w-16 h-16 text-primary/20" />
          </div>
        </div>
        
        <!-- Quick Actions -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          <div class="flex items-center p-4 bg-primary-bg/10 dark:bg-primary-dark-bg/10 rounded-lg">
            <Archive class="w-6 h-6 text-primary mr-3" />
            <div>
              <h3 class="font-medium text-primary-text dark:text-primary-dark-text">
                {{ totalAvailableModules }} Modules
              </h3>
              <p class="text-sm text-secondary-text dark:text-secondary-dark-text">
                Training materials assigned
              </p>
            </div>
          </div>

          <div class="flex items-center p-4 bg-primary-bg/10 dark:bg-primary-dark-bg/10 rounded-lg">
            <ClipboardList class="w-6 h-6 text-primary mr-3" />
            <div>
              <h3 class="font-medium text-primary-text dark:text-primary-dark-text">
                {{ modulesWithExams }} Exams
              </h3>
              <p class="text-sm text-secondary-text dark:text-secondary-dark-text">
                Assessments available
              </p>
            </div>
          </div>

          <div class="flex items-center p-4 bg-primary-bg/10 dark:bg-primary-dark-bg/10 rounded-lg">
            <Calendar class="w-6 h-6 text-primary mr-3" />
            <div>
              <h3 class="font-medium text-primary-text dark:text-primary-dark-text">
                Current Month
              </h3>
              <p class="text-sm text-secondary-text dark:text-secondary-dark-text">
                Module available in {{ currentMonthName }}
              </p>
            </div>
          </div>
        </div>
        
        <!-- Module Overview -->
        <div v-if="accessibleModules && (accessibleModules.current_month_modules.length > 0 || accessibleModules.previous_modules.length > 0)">
          <h3 class="text-md font-medium text-primary-text dark:text-primary-dark-text mb-4">
            Your Training Modules
          </h3>
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-64 overflow-y-auto training-modules-container">
            <!-- Current Month Modules -->
            <div v-for="module in accessibleModules.current_month_modules" :key="`current-${module.module_id}`" 
                 class="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-white dark:bg-gray-800">
              <div class="flex items-start justify-between mb-2">
                <h4 class="text-sm font-medium text-primary-text dark:text-primary-dark-text line-clamp-1">
                  {{ module.name }}
                </h4>
                <span class="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300">
                  Current
                </span>
              </div>
              <p class="text-xs text-secondary-text dark:text-secondary-dark-text mb-3 line-clamp-2">
                {{ module.description || 'No description available' }}
              </p>
              <div class="flex items-center justify-between">
                <div class="flex items-center space-x-2">
                  <span v-if="module.exam" class="text-xs text-blue-600 dark:text-blue-400">Has Exam</span>
                  <span v-else class="text-xs text-gray-500 dark:text-gray-400">No Exam</span>
                </div>
                <button @click="viewModuleRedirect" class="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200">
                  View →
                </button>
              </div>
            </div>
            <!-- Previous Modules (limit to 6 to save space) -->
            <div v-for="module in accessibleModules.previous_modules.slice(0, 6)" :key="`previous-${module.module_id}`" 
                 class="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-white dark:bg-gray-800">
              <div class="flex items-start justify-between mb-2">
                <h4 class="text-sm font-medium text-primary-text dark:text-primary-dark-text line-clamp-1">
                  {{ module.name }}
                </h4>
                <span class="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium" :class="{
                  'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300': module.completion_status === 'completed_passed',
                  'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300': module.completion_status === 'completed_failed',
                  'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300': module.completion_status === 'not_attempted',
                  'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300': module.completion_status === 'no_exam_required'
                }">
                  {{ getModuleStatusText(module.completion_status) }}
                </span>
              </div>
              <p class="text-xs text-secondary-text dark:text-secondary-dark-text mb-3 line-clamp-2">
                {{ module.description || 'No description available' }}
              </p>
              <div class="flex items-center justify-between">
                <div class="flex items-center space-x-2">
                  <span v-if="module.exam" class="text-xs text-blue-600 dark:text-blue-400">Has Exam</span>
                  <span v-else class="text-xs text-gray-500 dark:text-gray-400">No Exam</span>
                </div>
                <button @click="viewModuleRedirect" class="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200">
                  View →
                </button>
              </div>
            </div>
          </div>
          <div v-if="accessibleModules.previous_modules.length > 6" class="mt-4 text-center">
            <button @click="viewModuleRedirect" class="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200">
              View all {{ accessibleModules.previous_modules.length }} previous modules →
            </button>
          </div>
        </div>
        <div v-else class="text-center py-8">
          <Archive class="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p class="text-secondary-text dark:text-secondary-dark-text">
            No modules assigned for {{ currentYear }}. Contact your administrator for assignments.
          </p>
        </div>
      </div>

      <!-- Bottom Row -->
  <div class="flex flex-col lg:flex-row gap-6">
        <!-- Notifications -->
  <div class="card p-6 flex flex-col flex-1 h-[463px]">
          <div class="flex items-center justify-between mb-4">
            <h2 class="text-xl font-semibold text-primary-text dark:text-primary-dark-text flex items-center gap-2">
              Notifications
              <!-- Unread count badge -->
              <span 
                v-if="unreadActivitiesCount > 0" 
                class="inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-bold bg-blue-500 text-white"
                :title="`${unreadActivitiesCount} unread notification${unreadActivitiesCount > 1 ? 's' : ''}`"
              >
                {{ unreadActivitiesCount }}
              </span>
            </h2>
            <div class="flex items-center gap-2">
              <!-- Mark all as read button -->
              <button
                v-if="unreadActivitiesCount > 0"
                @click="handleMarkAllAsRead"
                class="px-3 py-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                title="Mark all as read"
              >
                Mark all read
              </button>
              <!-- Refresh button -->
              <button
                @click="handleRefreshActivities"
                class="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                title="Refresh notifications"
              >
                <svg class="w-4 h-4 text-secondary-text dark:text-secondary-dark-text" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
            </div>
          </div>
          <div v-if="displayActivities.length === 0" class="flex-1 flex items-center justify-center">
            <div class="text-center">
              <FileText class="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p class="text-secondary-text dark:text-secondary-dark-text">
                No recent activity to display
              </p>
            </div>
          </div>
          <div v-else class="flex-1 max-h-[48rem] overflow-y-auto space-y-4 pr-2 notifications-container pt-3">
            <div 
              v-for="activity in displayActivities" 
              :key="activity.id" 
              @click="handleActivityClick(activity)"
              :class="[
                // add right padding so the absolutely-positioned status badge doesn't overlap text
                'relative flex items-start gap-3 p-3 pr-24 rounded-lg transition-all duration-200 cursor-pointer',
                activity.isRead 
                  ? 'bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700' 
                  : 'bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 border-l-4 border-blue-500'
              ]"
            >
              <!-- New indicator -->
              <div 
                v-if="activity.isNew && !activity.isRead" 
                class="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full animate-pulse"
                title="New"
              ></div>
              <!-- Status Badge (moved to top-right) -->
              <span v-if="activity.status"
                :class="[ 'activity-status-badge absolute top-3 right-3 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium', getActivityBadgeClass(activity.status) ]"
              >
                {{ getActivityStatusLabel(activity.status) }}
              </span>
              
              <div class="flex-shrink-0">
                <div :class="getActivityIconBgClass(activity.type, activity.status)" class="w-10 h-10 rounded-full flex items-center justify-center">
                  <component :is="getActivityIcon(activity.type, activity.status)" :class="getActivityIconClass(activity.type, activity.status)" class="w-5 h-5" />
                </div>
              </div>
              <div class="ml-3 flex-1 min-w-0">
                <p :class="[
                  'text-sm mb-1',
                  activity.isRead ? 'font-normal text-secondary-text dark:text-secondary-dark-text' : 'font-semibold text-primary-text dark:text-primary-dark-text'
                ]">
                  {{ activity.description }}
                </p>
                <div class="flex items-center flex-wrap gap-2">
                  <p class="text-xs text-secondary-text dark:text-secondary-dark-text flex items-center">
                    <Clock class="w-3 h-3 mr-1" />
                    {{ formatDate(activity.timestamp) }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

  <!-- Current Month Modules - Enhanced -->
  <div class="card p-6 flex-1 flex flex-col h-full">
          <div class="flex items-center justify-between mb-4">
            <div>
              <h3 class="text-lg font-medium text-primary-text dark:text-primary-dark-text flex items-center">
                <Calendar class="w-5 h-5 mr-2 text-blue-500" />
                {{ currentMonthName }} Training
              </h3>
              <p class="text-sm text-secondary-text dark:text-secondary-dark-text">
                <span v-if="hasCurrentMonthAccess">{{ currentMonthModules.length }} module{{ currentMonthModules.length !== 1 ? 's' : '' }} available this month</span>
                <span v-else>No training scheduled this month</span>
              </p>
            </div>
            <div class="flex items-center space-x-2">
              <div v-if="hasCurrentMonthAccess" 
                   class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300 no-wrap-badge">
                <CheckCircle class="w-3 h-3 mr-1 hide-on-480" />
                <span class="badge-text">Active Access</span>
              </div>
              <div v-else 
                   class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300 no-wrap-badge">
                <XCircle class="w-3 h-3 mr-1 hide-on-480" />
                <span class="badge-text">No Access</span>
              </div>
            </div>
          </div>

          <!-- No Access State -->
          <div v-if="!hasCurrentMonthAccess" class="flex flex-col items-center justify-center flex-1 min-h-[200px] text-center p-8">
            <div class="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
              <Calendar class="w-8 h-8 text-gray-400" />
            </div>
            <h4 class="text-lg font-medium text-gray-600 dark:text-gray-400 mb-2">No Training This Month</h4>
            <p class="text-sm text-gray-500 dark:text-gray-500 mb-4 max-w-sm">
              You don't have any modules assigned for {{ currentMonthName }}. Check your assignment calendar or contact your administrator.
            </p>
            <div class="flex items-center text-xs text-blue-600 dark:text-blue-400">
              <Clock class="w-3 h-3 mr-1 hide-on-480" />
              Next available training periods may be in upcoming months
            </div>
          </div>

          <!-- No Modules State -->
          <div v-else-if="currentMonthModules.length === 0" class="flex flex-col items-center justify-center flex-1 min-h-[200px] text-center p-8">
            <div class="w-16 h-16 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center mb-4">
              <Archive class="w-8 h-8 text-blue-500" />
            </div>
            <h4 class="text-lg font-medium text-primary-text dark:text-primary-dark-text mb-2">Ready for Training</h4>
            <p class="text-sm text-secondary-text dark:text-secondary-dark-text mb-4 max-w-sm">
              You have access to training this month, but no modules are currently available. New modules may be added soon.
            </p>
            <div class="inline-flex items-center px-4 py-2 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300">
              <CheckCircle class="w-4 h-4 mr-2" />
              {{ currentMonthName }} access confirmed
            </div>
          </div>

          <!-- Modules Available -->
          <div v-else class="flex-1">
            <!-- Single Module - Featured Layout -->
            <div v-if="currentMonthModules.length === 1" class="space-y-4">
              <div v-for="moduleData in currentMonthModules" :key="moduleData.module_id" 
                   class="module-card relative overflow-hidden rounded-lg bg-white dark:bg-gray-800 p-6 border border-gray-200 dark:border-gray-700">
                
                <!-- Module Header -->
                <div class="flex items-start justify-between mb-4">
                  <div class="flex-1">
                    <div class="flex items-center mb-3">
                      <BookOpen class="w-5 h-5 text-primary mr-2" />
                      <span class="text-sm font-medium text-primary-text dark:text-primary-dark-text">Module this Month</span>
                    </div>
                    <h4 class="text-xl font-semibold text-primary-text dark:text-primary-dark-text mb-2">
                      {{ moduleData.name }}
                    </h4>
                    <p class="text-sm text-secondary-text dark:text-secondary-dark-text leading-relaxed">
                      {{ moduleData.description || 'No description available' }}
                    </p>
                  </div>
                </div>

                <!-- Module Details Grid -->
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                  <!-- Time Period -->
                  <div class="bg-white dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700 border-l-4 border-l-primary shadow-sm transition-all duration-200 hover:shadow-md flex flex-col">
                    <div class="flex items-center mb-3">
                      <Clock class="w-4 h-4 text-primary mr-2" />
                      <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Available Period</span>
                    </div>
                    <div class="flex-1 flex items-center justify-center">
                      <div v-if="moduleData.time_settings?.[0]" class="text-2xl text-primary-text dark:text-primary-dark-text font-semibold text-center">
                        {{ formatDateRange(moduleData.time_settings[0].start_date, moduleData.time_settings[0].end_date) }}
                      </div>
                      <div v-else class="text-lg text-gray-500 text-center">Not specified</div>
                    </div>
                  </div>

                  <!-- Exam Information -->
                  <div class="bg-white dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700 border-l-4 border-l-primary shadow-sm transition-all duration-200 hover:shadow-md">
                    <div class="flex items-center mb-2">
                      <ClipboardList class="w-4 h-4 text-primary mr-2" />
                      <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Assessment</span>
                    </div>
                    <div v-if="moduleData.exam" class="space-y-1">
                      <div class="text-sm font-medium text-primary-text dark:text-primary-dark-text">
                        {{ moduleData.exam.name }}
                      </div>
                      <div class="flex items-center text-xs text-gray-600 dark:text-gray-400">
                        <Clock class="w-3 h-3 mr-1" />
                        {{ moduleData.exam.time_limit_formatted }} • {{ moduleData.exam.passing_rate }}% to pass
                      </div>
                    </div>
                    <div v-else class="text-sm text-gray-500">No exam required</div>
                  </div>
                </div>

                <!-- Action Buttons -->
                <div class="flex items-center justify-between pt-2 border-t border-gray-100 dark:border-gray-700">
                  <div class="flex items-center space-x-2">
                    <BaseButton
                      :icon="BookOpen"
                      text="View Module"
                      variant="primary"
                      size="md"
                      @click="viewModuleRedirect"
                    />
                  </div>
                  <div class="text-xs text-gray-500 dark:text-gray-400">
                    Available until {{ formatEndDate(moduleData.time_settings?.[0]?.end_date) }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Multiple Modules - Compact Layout -->
            <div v-else class="space-y-3 max-h-[24rem] overflow-y-auto hide-scrollbar">
              <div v-for="moduleData in currentMonthModules" :key="moduleData.module_id" 
                   class="flex items-center justify-between p-4 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow duration-200">
                <div class="flex-1 min-w-0">
                  <div class="flex items-center mb-1">
                    <BookOpen class="w-4 h-4 text-blue-500 mr-2" />
                    <h4 class="font-medium text-primary-text dark:text-primary-dark-text truncate">
                      {{ moduleData.name }}
                    </h4>
                  </div>
                  <p class="text-xs text-secondary-text dark:text-secondary-dark-text truncate mb-2">
                    {{ moduleData.description || 'No description available' }}
                  </p>
                  <div class="flex items-center space-x-4 text-xs">
                    <div v-if="moduleData.time_settings?.[0]" class="flex items-center text-blue-600 dark:text-blue-400">
                      <Clock class="w-3 h-3 mr-1" />
                      {{ formatDateRange(moduleData.time_settings[0].start_date, moduleData.time_settings[0].end_date) }}
                    </div>
                    <div v-if="moduleData.exam" class="flex items-center text-purple-600 dark:text-purple-400">
                      <ClipboardList class="w-3 h-3 mr-1" />
                      {{ moduleData.exam.time_limit_formatted }}
                    </div>
                  </div>
                </div>
                <div class="ml-4 flex flex-col items-end space-y-2">
                  <span :class="{
                    'px-2 py-1 rounded text-xs font-semibold': true,
                    'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-300': getModuleActiveStatus(moduleData) === 'active',
                    'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-300': getModuleActiveStatus(moduleData) === 'inactive'
                  }">
                    {{ getModuleActiveStatus(moduleData) === 'active' ? 'Active' : 'Inactive' }}
                  </span>
                  <div class="flex items-center space-x-1">
                    <BaseButton :icon="BookOpen" text="View Module" variant="ghost" size="sm" @click="viewModuleRedirect" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useTitle } from '@/composables/ui/useTitle'
import { useUser } from '@/composables/auth/useUser'
import { useToastService } from '@/services/toast'
import { dashboardApi, type DashboardStats, type Activity } from '@/services/dashboard'
import { employeeAssignmentApi, type AccessibleModules } from '@/services/employeeAssignment'
import moduleAccessService, { type ModuleAccessRecord } from '@/services/moduleAccess'
import { useActivityPolling, type ActivityWithReadStatus } from '@/composables/notifications/useActivityPolling'

import {
  XCircle,
  Archive,
  CheckCircle,
  Clock,
  LayoutDashboard,
  FileText,
  ClipboardList,
  Calendar,
  BookOpen,
  Users,
  Award,
  AlertCircle,
  PlayCircle,
  UserPlus
} from 'lucide-vue-next'
import BaseButton from '@/components/common/BaseButton.vue'
import { LoadingState } from '@/components/common'

const appTitle = computed(() => process.env.VUE_APP_TITLE || 'Empathy Health Services, Inc.')
useTitle(`Dashboard - ${appTitle.value}`)

// Composables
const router = useRouter()
const toast = useToastService()
const { userName } = useUser()

// State
const loading = ref(true)
const error = ref<string | null>(null)
const stats = ref<DashboardStats>({
  totalModules: 0,
  totalExams: 0,
  passedExams: 0,
  failedExams: 0,
  skippedExams: 0,
  currentExams: 0,
  upcomingExams: 0
})
const recentActivity = ref<Activity[]>([])
const accessibleModules = ref<AccessibleModules | null>(null)
const currentYear = new Date().getFullYear()
const moduleAccessMap = ref<Map<number, ModuleAccessRecord>>(new Map())

// Activity polling with notifications
const {
  activities: polledActivities,
  unreadActivitiesCount,
  clearNewActivitiesCount,
  markAsRead,
  markAllAsRead,
  refresh: refreshActivities
} = useActivityPolling({
  enableToasts: false,
  autoStart: true,
  isAdmin: false
})

// Use polled activities as the source of truth
const displayActivities = computed<ActivityWithReadStatus[]>(() => {
  if (polledActivities.value.length > 0) {
    return polledActivities.value
  }
  // Fallback to recentActivity with default read status
  return recentActivity.value.map(activity => ({
    ...activity,
    isRead: false,
    isNew: false
  }))
})
// loadingStates removed — dashboard no longer starts modules directly

// Computed properties
const currentMonthName = computed(() => {
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ]
  return monthNames[new Date().getMonth()]
})

const totalAvailableModules = computed(() => {
  return accessibleModules.value?.accessible_modules_count || 0
})

const modulesWithExams = computed(() => {
  const current = accessibleModules.value?.current_month_modules.filter(m => m.exam) || []
  const previous = accessibleModules.value?.previous_modules.filter(m => m.exam) || []
  return current.length + previous.length
})

// (Removed passed/failed/skipped counts — display only month access pills as requested)

const currentMonthModules = computed(() => {
  return accessibleModules.value?.current_month_modules || []
})

const hasCurrentMonthAccess = computed(() => {
  return accessibleModules.value?.has_current_month_access || false
})

// Primary module name for current month and overflow helpers
const primaryCurrentMonthModule = computed(() => {
  const mods = currentMonthModules.value || []
  if (mods.length === 0) return ''
  // AccessibleModule has `name` property
  return mods[0].name || ''
})

const currentMonthModuleOverflow = computed(() => {
  const len = currentMonthModules.value?.length || 0
  return len > 1 ? len - 1 : 0
})

const currentMonthOtherModulesTooltip = computed(() => {
  if ((currentMonthModules.value?.length || 0) <= 1) return ''
  return currentMonthModules.value.slice(1).map(m => m.name || '').join(', ')
})

// Training progress helpers: group modules by month and show up to 3 months
const modulesByMonth = computed(() => {
  // Build a map of month name -> module names
  const map = new Map<string, string[]>()

  const pushModule = (monthName: string | undefined, moduleName: string | undefined) => {
    if (!monthName || !moduleName) return
  if (!map.has(monthName)) map.set(monthName, [])
  const arr = map.get(monthName)
  if (arr) arr.push(moduleName)
  }

  // Previous modules may include available_in_months — use those if present, otherwise group under 'Previous'
  (accessibleModules.value?.previous_modules || []).forEach(m => {
    if (m.available_in_months && m.available_in_months.length) {
      m.available_in_months.forEach(am => pushModule(am.name, m.name))
    } else {
      pushModule('Previous', m.name)
    }
  })

  // Current month modules grouped under current month name
  const currentName = currentMonthName.value
  ;(accessibleModules.value?.current_month_modules || []).forEach(m => pushModule(currentName, m.name))

  // Convert map to an array of { month, modules }
  return Array.from(map.entries()).map(([month, modules]) => ({ month, modules }))
})

// (We render all month groups via `modulesByMonth` above)

// Methods
const fetchDashboardData = async () => {
  try {
    loading.value = true
    error.value = null

    // Fetch accessible modules and activity data
    const [accessibleData, dashboardStats, activity] = await Promise.all([
      employeeAssignmentApi.getMyAccessibleModules(currentYear),
      dashboardApi.getStats(),
      dashboardApi.getRecentActivity()
    ])

    accessibleModules.value = accessibleData
    
    // Calculate stats from accessible modules data
    const currentModules = accessibleData.current_month_modules
    const previousModules = accessibleData.previous_modules
    
    // Update stats with backend data (backend already calculates these correctly from exam_results table)
    stats.value = {
      ...dashboardStats,
      totalModules: accessibleData.accessible_modules_count,
      totalExams: [...currentModules, ...previousModules].filter(m => m.exam).length,
      // Use backend-provided exam counts (from exam_results table) instead of calculating from completion_status
      // passedExams, failedExams, and skippedExams are already in dashboardStats
      currentExams: currentModules.length,
      upcomingExams: Math.max(0, accessibleData.accessible_modules_count - currentModules.length - previousModules.length)
    }
    
    recentActivity.value = activity
    
    // Load module access data for current month modules
    await loadAllModuleAccess()

  } catch (err) {
    error.value = 'Failed to load dashboard data. Please try again.'
    console.error('Dashboard data fetch error:', err)
  } finally {
    loading.value = false
  }
}

const formatDate = (timestamp: string) => {
  const date = new Date(timestamp)
  const now = new Date()
  const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60))

  if (diffInHours < 1) {
    return 'Just now'
  } else if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`
  } else {
    const diffInDays = Math.floor(diffInHours / 24)
    return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`
  }
}

const formatDateRange = (startDate: string, endDate: string) => {
  const start = new Date(startDate)
  const end = new Date(endDate)
  
  const startMonth = start.toLocaleDateString('en-US', { month: 'long' })
  const endMonth = end.toLocaleDateString('en-US', { month: 'long' })
  const startDay = start.getDate()
  const endDay = end.getDate()
  const year = end.getFullYear()
  
  if (start.getMonth() === end.getMonth()) {
    return `${startMonth} ${startDay}-${endDay}, ${year}`
  } else {
    return `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${year}`
  }
}



// Navigation functions
// Redirect to My Modules page
const viewModuleRedirect = () => {
  try {
    router.push('/my-modules')
  } catch (error) {
    console.error('Navigation error:', error)
    toast.error('Failed to navigate to My Modules')
  }
}

// Handle refresh activities and mark as viewed
const handleRefreshActivities = async () => {
  await refreshActivities()
  clearNewActivitiesCount()
}

// Handle activity click - mark as read
const handleActivityClick = (activity: any) => {
  if (!activity.isRead) {
    markAsRead(activity.id)
  }
}

// Handle mark all as read
const handleMarkAllAsRead = () => {
  markAllAsRead()
  clearNewActivitiesCount()
}

// Module navigation/start logic removed from dashboard. Use My Modules page for actions.

// Module Access Helper Methods
const loadModuleAccess = async (moduleId: number) => {
  try {
    const moduleAccess = await moduleAccessService.getModuleAccessStatus(moduleId)
    moduleAccessMap.value.set(moduleId, moduleAccess)
  } catch (error) {
    console.error('Error loading module access:', error)
  }
}

const loadAllModuleAccess = async () => {
  if (!currentMonthModules.value?.length) return
  
  try {
    const accessPromises = currentMonthModules.value.map(module => 
      loadModuleAccess(module.module_id)
    )
    await Promise.all(accessPromises)
  } catch (error) {
    console.error('Error loading module access data:', error)
  }
}

// Helper methods for module display
const getModuleStatusText = (completionStatus?: string) => {
  switch (completionStatus) {
    case 'completed_passed':
      return 'Passed'
    case 'completed_failed':
      return 'Failed'
    case 'not_attempted':
      return 'Skipped'
    case 'no_exam_required':
      return 'Complete'
    default:
      return 'Unknown'
  }
}

const getModuleActiveStatus = (module: any) => {
  if (module.completion_status === 'completed_passed' || module.completion_status === 'no_exam_required') {
    return 'active'
  } else if (module.completion_status === 'completed_failed' || module.completion_status === 'not_attempted') {
    return 'inactive'
  }
  // For current month modules, check if they have an active status
  return module.status === 1 ? 'active' : 'inactive'
}



const formatEndDate = (dateString?: string) => {
  if (!dateString) return 'N/A'
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  } catch {
    return 'Invalid date'
  }
}

// Note: Module start/exam actions removed from dashboard per requirement.

// Activity Icon Helper Methods
const getActivityIcon = (type: string, status?: string) => {
  switch (type) {
    case 'exam_result':
      return status === 'passed' ? Award : AlertCircle
    case 'module_completion':
      return CheckCircle
    case 'module_start':
      return PlayCircle
    case 'module_assignment':
      return UserPlus
    default:
      return Clock
  }
}

const getActivityIconClass = (type: string, status?: string) => {
  switch (type) {
    case 'exam_result':
      return status === 'passed' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
    case 'module_completion':
      return 'text-blue-600 dark:text-blue-400'
    case 'module_start':
      return 'text-purple-600 dark:text-purple-400'
    case 'module_assignment':
      return 'text-indigo-600 dark:text-indigo-400'
    default:
      return 'text-gray-600 dark:text-gray-400'
  }
}

const getActivityIconBgClass = (type: string, status?: string) => {
  switch (type) {
    case 'exam_result':
      return status === 'passed' 
        ? 'bg-green-100 dark:bg-green-900/20' 
        : 'bg-red-100 dark:bg-red-900/20'
    case 'module_completion':
      return 'bg-blue-100 dark:bg-blue-900/20'
    case 'module_start':
      return 'bg-purple-100 dark:bg-purple-900/20'
    case 'module_assignment':
      return 'bg-indigo-100 dark:bg-indigo-900/20'
    default:
      return 'bg-gray-100 dark:bg-gray-900/20'
  }
}

const getActivityBadgeClass = (status: string) => {
  switch (status) {
    case 'passed':
      return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300'
    case 'failed':
      return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300'
    case 'completed':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300'
    case 'in_progress':
      return 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300'
    case 'assigned':
      return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/20 dark:text-indigo-300'
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300'
  }
}

const getActivityStatusLabel = (status: string) => {
  switch (status) {
    case 'passed':
      return 'Passed'
    case 'failed':
      return 'Failed'
    case 'completed':
      return 'Completed'
    case 'in_progress':
      return 'In Progress'
    case 'assigned':
      return 'Assigned'
    default:
      return status
  }
}

// Lifecycle
onMounted(() => {
  fetchDashboardData()
})
</script>

<style scoped>
.card {
  background-color: white;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
  border: 1px solid #e5e7eb;
}

.dark .card {
  background-color: #1f2937;
  border-color: #374151;
}

/* Make scrollbar visible and styled for the notifications list */
.notifications-container {
  /* ensure the container creates a new stacking context for correct paint */
  position: relative;
}

/* WebKit-based browsers (Chrome, Edge, Safari) */
.notifications-container::-webkit-scrollbar {
  width: 12px;
}
.notifications-container::-webkit-scrollbar-track {
  background: transparent;
}
.notifications-container::-webkit-scrollbar-thumb {
  background-color: rgba(0,0,0,0.12);
  border-radius: 8px;
  border: 3px solid transparent; /* provide padding inside the track */
  background-clip: padding-box;
}
.notifications-container:hover::-webkit-scrollbar-thumb {
  background-color: rgba(0,0,0,0.2);
}

/* Firefox */
.notifications-container {
  scrollbar-width: thin;
  scrollbar-color: rgba(0,0,0,0.12) transparent;
}

/* Dark mode tweaks */
:deep(.dark) .notifications-container::-webkit-scrollbar-thumb {
  background-color: rgba(255,255,255,0.08);
}
:deep(.dark) .notifications-container:hover::-webkit-scrollbar-thumb {
  background-color: rgba(255,255,255,0.14);
}
:deep(.dark) .notifications-container {
  scrollbar-color: rgba(255,255,255,0.08) transparent;
}

/* Make scrollbar visible and styled for the training modules list */
.training-modules-container {
  /* ensure the container creates a new stacking context for correct paint */
  position: relative;
}

/* WebKit-based browsers (Chrome, Edge, Safari) */
.training-modules-container::-webkit-scrollbar {
  width: 12px;
}
.training-modules-container::-webkit-scrollbar-track {
  background: transparent;
}
.training-modules-container::-webkit-scrollbar-thumb {
  background-color: rgba(0,0,0,0.12);
  border-radius: 8px;
  border: 3px solid transparent; /* provide padding inside the track */
  background-clip: padding-box;
}
.training-modules-container:hover::-webkit-scrollbar-thumb {
  background-color: rgba(0,0,0,0.2);
}

/* Firefox */
.training-modules-container {
  scrollbar-width: thin;
  scrollbar-color: rgba(0,0,0,0.12) transparent;
}

/* Dark mode tweaks */
:deep(.dark) .training-modules-container::-webkit-scrollbar-thumb {
  background-color: rgba(255,255,255,0.08);
}
:deep(.dark) .training-modules-container:hover::-webkit-scrollbar-thumb {
  background-color: rgba(255,255,255,0.14);
}
:deep(.dark) .training-modules-container {
  scrollbar-color: rgba(255,255,255,0.08) transparent;
}

.hide-scrollbar {
  scrollbar-width: none;
  -ms-overflow-style: none;
}
.hide-scrollbar::-webkit-scrollbar {
  display: none;
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Enhanced interactions and animations */
.card:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
}

button:hover {
  transform: translateY(-1px);
  transition: all 0.2s ease;
}

button:active {
  transform: translateY(0);
}

/* Module card styling */
.module-card {
  transition: all 0.2s ease;
}

.module-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  border-color: #d1d5db;
}

.dark .module-card:hover {
  border-color: #4b5563;
}

/* Pulse animation for new activity badge */
@keyframes pulse-scale {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}

.animate-pulse {
  animation: pulse-scale 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

/* Smooth transitions for refresh button */
button svg {
  transition: transform 0.3s ease;
}

button:active svg {
  transform: rotate(180deg);
}

/* Read/Unread activity styles */
.activity-item-unread {
  border-left: 4px solid #3b82f6;
}

/* New activity pulse */
@keyframes new-pulse {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.1);
  }
}

/* Smooth click feedback */
.activity-item-clickable:active {
  transform: scale(0.98);
}

/* Positioning for top-right status badge inside notifications */
.activity-status-badge {
  z-index: 10;
  /* ensure the badge doesn't overlap the text: give it a small shadow and a max width */
  box-shadow: 0 1px 2px rgba(0,0,0,0.06);
  max-width: 9rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Small-screen tweaks: hide some icons under 480px and prevent badges from wrapping */
@media (max-width: 480px) {
  .hide-on-480 {
    display: none !important;
  }

  /* Make badge contents stay on one line and avoid wrapping to the next line */
  .no-wrap-badge {
    white-space: nowrap;
  }
  .no-wrap-badge .badge-text {
    display: inline-block;
    vertical-align: middle;
  }
}

/* Make info/access boxes inside the access card stack on mobile */
@media (max-width: 768px) {
  .card .grid.grid-cols-1.md\:grid-cols-2.lg\:grid-cols-3 {
    grid-template-columns: 1fr !important;
  }
}
</style>
